import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isChunkLoadError, reloadForFreshChunk } from '@/lib/chunk-error';

/**
 * ErrorBoundary RAÍZ (envuelve toda la App en main.tsx). Red de seguridad final:
 * cualquier error de render que no capture un boundary inferior (login, callback,
 * el propio AppLayout, o un fallo de carga de chunk a nivel del Suspense de App)
 * aterriza aquí en vez de dejar la pantalla en blanco.
 *
 * Si el error es un chunk viejo (tras deploy), auto-recarga a la versión nueva.
 * Para el resto, muestra un fallback amable con recarga manual.
 */
interface Props { children: ReactNode }
interface State { error: Error | null; reloading: boolean }

export default class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null, reloading: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, reloading: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[AppErrorBoundary]', error, info);
    if (isChunkLoadError(error)) {
      // Si el guard anti-bucle frena la recarga (ya recargamos hace <12s y volvió
      // a fallar), NO dejamos el spinner eterno: caemos al fallback manual.
      const reloaded = reloadForFreshChunk();
      if (!reloaded) this.setState({ reloading: false });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    if (this.state.reloading) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-6 text-center">
          <div className="h-6 w-6 rounded-full border-2 border-emerald-400/40 border-t-emerald-400 animate-spin" />
          <p className="text-sm text-pelip-text-soft">Cargando la versión más reciente…</p>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full rounded-lg border border-pelip-border-strong bg-pelip-bg-elev-1 p-6 text-center">
          <h1 className="text-lg font-semibold text-pelip-text mb-2">Algo ha fallado</h1>
          <p className="text-sm text-pelip-text-soft mb-4">
            La aplicación encontró un error inesperado. Recargar suele resolverlo.
          </p>
          <p className="text-xs text-pelip-text-faint font-mono break-all mb-4">
            {this.state.error.message}
          </p>
          <div className="flex gap-2 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 text-sm rounded-md bg-emerald-500/90 text-white hover:bg-emerald-500"
            >
              Recargar
            </button>
            <button
              onClick={() => { window.location.href = '/proyectos'; }}
              className="px-4 py-2 text-sm rounded-md border border-pelip-border-strong text-pelip-text-soft hover:bg-pelip-bg-elev-2"
            >
              Ir a Proyectos
            </button>
          </div>
        </div>
      </div>
    );
  }
}
